import React, { memo, useContext } from 'react';
import { branch, compose, defaultProps, mapProps, renameProp, setDisplayName, withHandlers, withProps } from 'recompose';
import { omit, pick, prop } from 'ramda';
import { useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { Game as CommonGame } from 'js/components/common/Game';
import withGameHover from 'js/components/main/Games/Game/withGameHover';
import withBounceInAnimation from 'js/components/main/Games/Game/withBounceInAnimation';
import Jackpot from '../Jackpot';
import { CATEGORY_TYPE } from 'js/components/main/Games/constants';
import { CategoryContext } from 'js/components/main/Games/context';
import { usePortletConfig, usePortletSelector } from 'js/components/system/Portlet';

import style from './index.scss';

export const withCategory = compose(
    withProps(() => ({
        ...useUserSelector(pick(['isLoggedIn'])),
        ...pick([
            'type',
            'gameSize',
            'withBounceInAnimation'
        ], useContext(CategoryContext)),
        isDesktop: useDeviceSelector(prop('isDesktop')),
        showFavorites: prop('showFavorites', usePortletConfig())
    })),
    renameProp('gameSize', 'size'),
    mapProps((props) => {
            const { type, showFavorites, isLoggedIn, isDesktop } = props;
            const isRecentlyPlayed = type === CATEGORY_TYPE.RECENTLY;
            const isFavorites = type === CATEGORY_TYPE.FAVORITES;
            const isPinned = type === CATEGORY_TYPE.PINNED;
            const commonGameCls = !isDesktop && !isPinned ? style.game15 : '';
            const gameFeed = usePortletSelector(prop('feedName'));

            return {
                ...props,
                gameFeed,
                isHoverEnabled: isDesktop,
                isPinned,
                showFavoriteIcon: showFavorites && isLoggedIn,
                showGameInfo: !isDesktop && !isRecentlyPlayed && !isFavorites,
                showRecentlyPlayedIcon: isRecentlyPlayed,
                commonGameCls
            };
        }
    )
);

export const GameWithJackpot = ({
    code,
    size,
    animate,
    className = '',
    commonGameCls = '',
    jackpotCls = '',
    gameFeed,
    showFavoriteIcon,
    showFeatureIcons,
    showGameInfo,
    showRecentlyPlayedIcon,
    withFlexWidth,
    setGameRef,
    setGameImageRef,
    setNgpJackpotRef,
    onMouseMove,
    isMobileSpecialCategory,
    children,
    displayName,
    mostPlayedGame,
    isPinned
}) => {
    const { showJackpots, showGuaranteedHitTime, showGreenTubeJackpots, showSkyWindJackpots } = usePortletConfig();

    return (
        <div className={`${style.container} ${className}`} ref={setGameRef}>
            <CommonGame
                className={`${withFlexWidth ? style.game : ''} ${commonGameCls || ''} ${animate ? 'lighterBounceIn' : ''}`}
                setGameImageRef={setGameImageRef}
                displayName={displayName}
                mostPlayedGame={mostPlayedGame}
                {...{
                    code,
                    size,
                    isPinned,
                    showRecentlyPlayedIcon,
                    gameFeed,
                    showFavoriteIcon,
                    showFeatureIcons,
                    showGameInfo,
                    onMouseMove,
                    isMobileSpecialCategory
                }}
            />
            {children}
            {<Jackpot
                gameCode={code}
                className={`${style.jackpot} ${jackpotCls}`}
                showGuaranteedHitTime={showGuaranteedHitTime}
                setNgpJackpotRef={setNgpJackpotRef}
                showGreenTube={showGreenTubeJackpots}
                showSkyWind={showSkyWindJackpots}
                isRegularJackpot={showJackpots}
            />}
        </div>
    );
};

export const NgpGame = compose(
    setDisplayName('NgpGame'),
    defaultProps({
        className: '',
        withFlexWidth: true
    }),
    withHandlers(({ setGameRef }) => {
        let gameWrapperRef;

        return {
            setGameRef: () => (ref) => {
                gameWrapperRef = ref;
                setGameRef && setGameRef(gameWrapperRef);
            },
            getGameRef: () => () => gameWrapperRef
        };
    }),
    withBounceInAnimation(),
    branch(
        props => props.isHoverEnabled,
        withGameHover
    ),
    mapProps(omit(['isHoverEnabled']))
)(GameWithJackpot);

export default compose(
    memo,
    setDisplayName('NgpGameWithCategory'),
    withCategory
)(NgpGame);
